import type { AxiosError } from "axios";
import { CircleAlert, Loader, Sparkles, Ticket } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  type AiStudyBookingCertificatePreview,
  type BookingCertificatePreview,
  useAiStudyBookingCertificatePreview,
  useBookingCertificatePreview,
  useGenerateAiStudyBookingCertificates,
  useGenerateBookingCertificates,
} from "../api/use-claim-actions";

/**
 * « Générer le Certificat de réservation » — two sources, the jurist picks one:
 * - « Étude Insight IA » (default): the original booking matched by the incident study against
 *   the PNR history, rendered server-side. Also what « Confirmer l'incident » regenerates.
 * - « GDS »: port of the TopMenu.vue action (getBookingCertificatePreview →
 *   postBookingCertificates), from the synced tickets.
 */

type CertificateSource = "ai-study" | "gds";

function formatDate(value: unknown) {
  return new Date(String(value)).toLocaleDateString("fr-FR");
}

function PreviewError({ error, fallback }: { error: unknown; fallback: string }) {
  return (
    <p className="py-4 text-sm text-red-600">
      {(error as AxiosError<{ message?: string }>)?.response?.data?.message ?? fallback}
    </p>
  );
}

function NoTicket() {
  return (
    <div className="flex items-center gap-3 rounded-md border border-blue-200 bg-blue-50 px-4 py-3">
      <CircleAlert className="h-5 w-5 shrink-0 text-blue-500" />
      <p className="text-sm text-blue-900">Aucun ticket détecté</p>
    </div>
  );
}

function PreviewLoading() {
  return (
    <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-500">
      <Spinner /> Chargement de l'aperçu…
    </div>
  );
}

function AiStudyTicketRow({ ticket }: { ticket: AiStudyBookingCertificatePreview["tickets"][number] }) {
  return (
    <li className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-slate-800">
          {ticket.firstName} {ticket.lastName}
        </span>
        <span className="font-mono text-xs text-slate-500">{ticket.ticketNumber}</span>
      </div>
      {ticket.issueDate ? (
        <div className="mt-1 text-xs text-slate-500">Émis le {formatDate(ticket.issueDate)}</div>
      ) : null}
      <ul className="mt-2 space-y-0.5">
        {ticket.flights.map((flight) => (
          <li
            key={`${flight.flightIdentifier}-${flight.departureAirportIata}`}
            className="text-xs text-slate-600"
          >
            <span className="font-medium">{flight.flightIdentifier}</span> {flight.departureAirportIata} →{" "}
            {flight.arrivalAirportIata}
            {flight.scheduledDepartureUTC ? (
              <span className="text-slate-400"> — {formatDate(flight.scheduledDepartureUTC)}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </li>
  );
}

function TicketPreviewRow({ ticket }: { ticket: BookingCertificatePreview["tickets"][number] }) {
  return (
    <li className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-slate-800">
          {ticket.firstName} {ticket.lastName}
        </span>
        <span className="font-mono text-xs text-slate-500">{ticket.ticketNumber}</span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
        <span>Émis le {formatDate(ticket.issueDate)}</span>
        {ticket.issuingAirlineName ? <span>{ticket.issuingAirlineName}</span> : null}
      </div>
      <ul className="mt-2 space-y-0.5">
        {ticket.itinerary.map((leg) => (
          <li key={leg.flightId} className="text-xs text-slate-600">
            <span className="font-medium">{leg.flight?.flightIdentifier}</span>{" "}
            {leg.flight?.departureAirport?.iata} → {leg.flight?.arrivalAirport?.iata}
            {leg.flight?.scheduledDepartureLocal ? (
              <span className="text-slate-400"> — {formatDate(leg.flight.scheduledDepartureLocal)}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </li>
  );
}

interface BookingCertificateDialogProps {
  claimId: string;
  open: boolean;
  onClose: () => void;
}

export function BookingCertificateDialog({ claimId, open, onClose }: BookingCertificateDialogProps) {
  const [source, setSource] = useState<CertificateSource>("ai-study");
  const aiStudyPreview = useAiStudyBookingCertificatePreview(claimId, open);
  const preview = useBookingCertificatePreview(claimId, open && source === "gds");
  const generateFromAiStudy = useGenerateAiStudyBookingCertificates(claimId);
  const generateCertificates = useGenerateBookingCertificates(claimId);

  const aiStudyTickets = aiStudyPreview.data?.tickets ?? [];
  const tickets = preview.data?.tickets ?? [];
  const isPending = generateFromAiStudy.isPending || generateCertificates.isPending;
  const canGenerate = source === "ai-study" ? aiStudyTickets.length > 0 : tickets.length > 0;

  function handleGenerate() {
    const onSuccess = ({ success }: { success: boolean }) => {
      if (success) {
        onClose();
      }
    };
    if (source === "ai-study") {
      generateFromAiStudy.mutate(undefined, { onSuccess });
      return;
    }
    if (!preview.data) {
      return;
    }
    generateCertificates.mutate(preview.data, { onSuccess });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Générer le Certificat de réservation</DialogTitle>
          <DialogDescription>
            Aperçu des billets qui figureront sur le(s) certificat(s) avant génération.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel>
          <Tabs value={source} onValueChange={(value) => setSource(value as CertificateSource)}>
            <TabsList>
              <TabsTrigger value="ai-study">
                <Sparkles /> Étude Insight IA
              </TabsTrigger>
              <TabsTrigger value="gds">GDS</TabsTrigger>
            </TabsList>
            <TabsContent value="ai-study" className="pt-4">
              {aiStudyPreview.isLoading ? (
                <PreviewLoading />
              ) : aiStudyPreview.isError ? (
                <PreviewError
                  error={aiStudyPreview.error}
                  fallback="Impossible de charger l'aperçu depuis l'étude Insight IA."
                />
              ) : aiStudyTickets.length === 0 ? (
                <NoTicket />
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-slate-500">
                    Réservation d'origine retrouvée par l'étude dans l'historique du PNR{" "}
                    <span className="font-mono text-slate-800">{aiStudyPreview.data?.gdsPnrId}</span>, avant
                    tout réacheminement.
                  </p>
                  <ul className="space-y-2">
                    {aiStudyTickets.map((ticket) => (
                      <AiStudyTicketRow key={ticket.ticketNumber} ticket={ticket} />
                    ))}
                  </ul>
                </div>
              )}
            </TabsContent>
            <TabsContent value="gds" className="pt-4">
              {preview.isLoading ? (
                <PreviewLoading />
              ) : preview.isError ? (
                <PreviewError
                  error={preview.error}
                  fallback="Impossible de charger l'aperçu du certificat."
                />
              ) : tickets.length === 0 ? (
                <NoTicket />
              ) : (
                <div className="space-y-4">
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <dt className="text-slate-500">GDS</dt>
                    <dd className="text-slate-800">{preview.data?.gdsName}</dd>
                    <dt className="text-slate-500">PNR</dt>
                    <dd className="font-mono text-slate-800">{preview.data?.gdsPnrId}</dd>
                    <dt className="text-slate-500">Agence</dt>
                    <dd className="text-slate-800">{preview.data?.travelAgencyName}</dd>
                  </dl>
                  <ul className="space-y-2">
                    {tickets.map((ticket) => (
                      <TicketPreviewRow key={ticket.ticketNumber} ticket={ticket} />
                    ))}
                  </ul>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogPanel>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button disabled={!canGenerate || isPending} onClick={handleGenerate}>
            {isPending ? <Loader className="animate-spin" /> : <Ticket />}
            Générer le certificat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
